import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Smartphone, Save, RotateCcw, Apple, Bot, History, ShieldAlert } from 'lucide-react';
import { adminAppVersion } from '@/api/admin';
import type {
  AppPlatformRelease,
  AppVersionConfig,
  UpdateAppVersionRequest,
} from '@/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { ApiError } from '@/lib/http';

type Platform = 'android' | 'ios';

/**
 * 语义版本比较:a < b 返回 true(按 major.minor.patch 数值逐段比较)。
 * 非数字段按 0 处理;缺段补 0("1.2" 视作 "1.2.0")。仅用于前端「是否抬高 min」提示,
 * 最终以后端校验为准。
 */
function isVersionBelow(a: string, b: string): boolean {
  const pa = String(a ?? '').split('.').map((x) => parseInt(x, 10) || 0);
  const pb = String(b ?? '').split('.').map((x) => parseInt(x, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const va = pa[i] ?? 0;
    const vb = pb[i] ?? 0;
    if (va < vb) return true;
    if (va > vb) return false;
  }
  return false;
}

/** 把后端返回的平台对象拷成可编辑草稿(去掉只读的 history)。 */
function toDraft(r: AppPlatformRelease): AppPlatformRelease {
  return {
    latestVersion: r.latestVersion ?? '',
    minSupportedVersion: r.minSupportedVersion ?? '',
    releaseDate: r.releaseDate ?? '',
    size: r.size ?? '',
    downloadUrl: r.downloadUrl ?? '',
    testflightUrl: r.testflightUrl ?? '',
    appStoreUrl: r.appStoreUrl ?? '',
    releaseNotes: r.releaseNotes ?? '',
  };
}

/** 平台草稿 → 提交体(不带 history;空字符串归一为 undefined 不提交)。 */
function toInput(d: AppPlatformRelease): AppPlatformRelease {
  const s = (v?: string) => {
    const t = (v ?? '').trim();
    return t.length ? t : undefined;
  };
  return {
    latestVersion: (d.latestVersion ?? '').trim(),
    minSupportedVersion: (d.minSupportedVersion ?? '').trim(),
    releaseDate: s(d.releaseDate),
    size: s(d.size),
    downloadUrl: s(d.downloadUrl),
    testflightUrl: s(d.testflightUrl),
    appStoreUrl: s(d.appStoreUrl),
    releaseNotes: s(d.releaseNotes),
  };
}

/** 平台两草稿是否有差异(逐字段比较;history 不参与)。 */
function platformDirty(a: AppPlatformRelease, b: AppPlatformRelease): boolean {
  const keys: Array<keyof AppPlatformRelease> = [
    'latestVersion',
    'minSupportedVersion',
    'releaseDate',
    'size',
    'downloadUrl',
    'testflightUrl',
    'appStoreUrl',
    'releaseNotes',
  ];
  return keys.some((k) => (a[k] ?? '') !== (b[k] ?? ''));
}

export function AppVersion() {
  const qc = useQueryClient();
  const cfgQ = useQuery({ queryKey: ['admin', 'app-version'], queryFn: adminAppVersion.get });

  const [android, setAndroid] = useState<AppPlatformRelease | null>(null);
  const [ios, setIos] = useState<AppPlatformRelease | null>(null);
  const [note, setNote] = useState('');
  const [blockUnknown, setBlockUnknown] = useState(false);

  useEffect(() => {
    if (cfgQ.data) {
      setAndroid(toDraft(cfgQ.data.android));
      setIos(toDraft(cfgQ.data.ios));
      setBlockUnknown(cfgQ.data.blockUnknownVersion ?? true);
    }
  }, [cfgQ.data]);

  const androidDirty = useMemo(
    () => (android && cfgQ.data ? platformDirty(android, toDraft(cfgQ.data.android)) : false),
    [android, cfgQ.data],
  );
  const iosDirty = useMemo(
    () => (ios && cfgQ.data ? platformDirty(ios, toDraft(cfgQ.data.ios)) : false),
    [ios, cfgQ.data],
  );
  const loadedBlock = cfgQ.data?.blockUnknownVersion ?? true;
  const blockDirty = blockUnknown !== loadedBlock;
  const dirty = androidDirty || iosDirty || blockDirty;

  // 「是否相对已加载值抬高了某平台 minSupportedVersion」——据此在保存前给出显著风险提示。
  const androidMinRaised = useMemo(
    () =>
      !!(android && cfgQ.data) &&
      isVersionBelow(cfgQ.data.android.minSupportedVersion, android.minSupportedVersion),
    [android, cfgQ.data],
  );
  const iosMinRaised = useMemo(
    () =>
      !!(ios && cfgQ.data) &&
      isVersionBelow(cfgQ.data.ios.minSupportedVersion, ios.minSupportedVersion),
    [ios, cfgQ.data],
  );
  const minRaised = androidMinRaised || iosMinRaised;

  const submit = useMutation({
    mutationFn: () => {
      const body: UpdateAppVersionRequest = {};
      if (androidDirty && android) body.android = toInput(android);
      if (iosDirty && ios) body.ios = toInput(ios);
      if (blockDirty) body.blockUnknownVersion = blockUnknown;
      if (note.trim()) body.note = note.trim();
      return adminAppVersion.update(body);
    },
    onSuccess: (data: AppVersionConfig) => {
      toast.success('已保存,App 即时生效', {
        description: '各平台版本策略已写入并对 App 立即生效',
      });
      qc.setQueryData(['admin', 'app-version'], data);
      qc.invalidateQueries({ queryKey: ['admin', 'app-version'] });
      setNote('');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : '';
      const reason =
        err instanceof ApiError ? (err.details as { reason?: string } | undefined)?.reason : undefined;
      let friendly = msg || '请稍后重试';
      if (reason === 'min_exceeds_latest') {
        friendly = '最低强制版本不能高于最新版本,请检查两者大小关系';
      } else if (reason === 'bad_version') {
        friendly = '版本号格式非法,请使用 major.minor.patch(如 1.0.2)';
      }
      toast.error('保存失败', { description: friendly });
    },
  });

  const reset = () => {
    if (!cfgQ.data) return;
    setAndroid(toDraft(cfgQ.data.android));
    setIos(toDraft(cfgQ.data.ios));
    setBlockUnknown(cfgQ.data.blockUnknownVersion ?? true);
    setNote('');
  };

  if (cfgQ.isLoading) {
    return <div className="p-6 text-sm text-ink-2">加载中…</div>;
  }
  if (cfgQ.isError) {
    return (
      <div className="p-6 text-sm text-down">
        加载失败:{cfgQ.error instanceof ApiError ? cfgQ.error.message : '请稍后重试'}
      </div>
    );
  }
  if (!android || !ios || !cfgQ.data) return null;

  const canSubmit = dirty && !submit.isPending;

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
          <Smartphone className="h-5 w-5 text-brand" />
          App 版本管理
        </h1>
        <p className="text-sm text-ink-2 mt-1">
          设置 App 各平台版本策略,保存即时生效。抬高「最低强制版本」会强制低于该版本的 App
          升级。历史版本由后端自动维护,无需手填。
        </p>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* ── Android ── */}
        <PlatformCard
          platform="android"
          title="Android"
          icon={<Bot className="h-4 w-4 text-up" />}
          draft={android}
          onChange={setAndroid}
          minRaised={androidMinRaised}
          loadedHistory={cfgQ.data.android.history}
          disabled={submit.isPending}
        />

        {/* ── iOS ── */}
        <PlatformCard
          platform="ios"
          title="iOS"
          icon={<Apple className="h-4 w-4 text-ink-1" />}
          draft={ios}
          onChange={setIos}
          minRaised={iosMinRaised}
          loadedHistory={cfgQ.data.ios.history}
          disabled={submit.isPending}
        />
      </div>

      {/* ── 保存区:备注 + 按钮 ── */}
      <Card>
        <CardHeader>
          <CardTitle>提交变更</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {minRaised && (
            <div className="rounded-md border border-warn/40 bg-warn/10 px-4 py-3 flex items-start gap-2.5 text-sm text-warn-dim">
              <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                <div className="font-semibold">检测到抬高「最低强制版本」</div>
                <div className="text-xs leading-relaxed">
                  {androidMinRaised && <span className="mr-2">Android</span>}
                  {iosMinRaised && <span className="mr-2">iOS</span>}
                  的最低强制版本被抬高 = 强制旧版本升级,保存后立即对 App 生效,并记入审计。
                </div>
              </div>
            </div>
          )}

          {/* 硬挡开关:逼停不带版本头的老版本(1.0.x) */}
          <div className="rounded-md border border-down/40 bg-down/5 px-4 py-3 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-down" />
                <span className="text-sm font-semibold text-ink-0">硬挡老版本(缺版本头)</span>
              </div>
              <Switch
                checked={blockUnknown}
                onCheckedChange={setBlockUnknown}
                disabled={submit.isPending}
              />
            </div>
            <p className="text-xs leading-relaxed text-ink-2">
              开启后,不带版本头的老版本(如 1.0.x,无升级闸代码)发起的业务请求一律回 426 被拦。
              <span className="text-down font-semibold">
                {' '}⚠ 老版本不认 426、只会报通用错误(连平仓都失败 = 软砖),且无升级引导;务必在新版 App
                已发布、用户能升级后再开启,否则会把全部老版本用户打废。
              </span>
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="apv-note">备注(写入审计)</Label>
              <Input
                id="apv-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="变更原因 / 工单号"
                disabled={submit.isPending}
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 pt-2 border-t border-line">
            <span className="text-xs text-ink-3">
              {dirty ? '有未保存的改动' : '无改动'}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" disabled={!dirty || submit.isPending} onClick={reset}>
                <RotateCcw className="h-4 w-4" />
                重置
              </Button>
              <Button onClick={() => submit.mutate()} disabled={!canSubmit}>
                <Save className="h-4 w-4" />
                {submit.isPending ? '保存中…' : '保存,App 即时生效'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface PlatformCardProps {
  platform: Platform;
  title: string;
  icon: React.ReactNode;
  draft: AppPlatformRelease;
  onChange: (next: AppPlatformRelease) => void;
  minRaised: boolean;
  loadedHistory?: AppVersionConfig['android']['history'];
  disabled: boolean;
}

function PlatformCard({
  platform,
  title,
  icon,
  draft,
  onChange,
  minRaised,
  loadedHistory,
  disabled,
}: PlatformCardProps) {
  const set = (patch: Partial<AppPlatformRelease>) => onChange({ ...draft, ...patch });
  const isAndroid = platform === 'android';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="最新版本" htmlFor={`${platform}-latest`}>
            <Input
              id={`${platform}-latest`}
              value={draft.latestVersion}
              onChange={(e) => set({ latestVersion: e.target.value })}
              placeholder="1.0.2"
              className="num"
              disabled={disabled}
            />
          </Field>
          <Field
            label="最低强制版本"
            htmlFor={`${platform}-min`}
            hint={minRaised ? '已抬高 · 保存即强制升级' : '低于此版本强制升级'}
            hintTone={minRaised ? 'warn' : undefined}
          >
            <Input
              id={`${platform}-min`}
              value={draft.minSupportedVersion}
              onChange={(e) => set({ minSupportedVersion: e.target.value })}
              placeholder="1.0.1"
              className="num"
              disabled={disabled}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="更新时间" htmlFor={`${platform}-date`}>
            <Input
              id={`${platform}-date`}
              type="date"
              value={draft.releaseDate ?? ''}
              onChange={(e) => set({ releaseDate: e.target.value })}
              className="num"
              disabled={disabled}
            />
          </Field>
          {isAndroid ? (
            <Field label="安装包大小" htmlFor={`${platform}-size`}>
              <Input
                id={`${platform}-size`}
                value={draft.size ?? ''}
                onChange={(e) => set({ size: e.target.value })}
                placeholder="99 MB"
                disabled={disabled}
              />
            </Field>
          ) : (
            <div /> /* iOS 不展示大小,占位保持网格对齐 */
          )}
        </div>

        {isAndroid ? (
          <Field label="下载地址" htmlFor={`${platform}-download`} hint="APK / 下载页地址">
            <Input
              id={`${platform}-download`}
              value={draft.downloadUrl ?? ''}
              onChange={(e) => set({ downloadUrl: e.target.value })}
              placeholder="https://superaios.com/downloads/app.apk"
              disabled={disabled}
            />
          </Field>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="TestFlight 地址" htmlFor={`${platform}-testflight`}>
              <Input
                id={`${platform}-testflight`}
                value={draft.testflightUrl ?? ''}
                onChange={(e) => set({ testflightUrl: e.target.value })}
                placeholder="https://testflight.apple.com/join/xxxx"
                disabled={disabled}
              />
            </Field>
            <Field label="App Store 地址" htmlFor={`${platform}-appstore`}>
              <Input
                id={`${platform}-appstore`}
                value={draft.appStoreUrl ?? ''}
                onChange={(e) => set({ appStoreUrl: e.target.value })}
                placeholder="https://apps.apple.com/app/idxxxxxxxx"
                disabled={disabled}
              />
            </Field>
          </div>
        )}

        <Field label="更新说明" htmlFor={`${platform}-notes`}>
          <textarea
            id={`${platform}-notes`}
            value={draft.releaseNotes ?? ''}
            onChange={(e) => set({ releaseNotes: e.target.value })}
            placeholder="本次更新内容(每行一条)"
            rows={4}
            disabled={disabled}
            className="flex w-full rounded-md border border-line bg-bg-3 px-3 py-2 text-sm text-ink-1 shadow-sm transition-colors placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-50 resize-y"
          />
        </Field>

        {/* 历史版本 — 只读(后端在 latestVersion 变更时自动前插),不参与提交 */}
        {loadedHistory && loadedHistory.length > 0 && (
          <div className="pt-2 border-t border-line">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-2 mb-2">
              <History className="h-3.5 w-3.5" />
              历史版本(只读 · 后端自动维护)
            </div>
            <ul className="space-y-1.5">
              {loadedHistory.map((h, i) => (
                <li key={`${h.version}-${i}`} className="text-xs text-ink-2 flex flex-wrap items-baseline gap-x-2">
                  <span className="num font-semibold text-ink-1">{h.version}</span>
                  {h.releaseDate && <span className="num text-ink-3">{h.releaseDate}</span>}
                  {h.size && <span className="text-ink-3">{h.size}</span>}
                  {h.releaseNotes && <span className="text-ink-3 truncate max-w-full">{h.releaseNotes}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  hint?: string;
  hintTone?: 'warn';
  children: React.ReactNode;
}

function Field({ label, htmlFor, hint, hintTone, children }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={htmlFor}>{label}</Label>
        {hint && (
          <span className={hintTone === 'warn' ? 'text-[11px] text-warn-dim' : 'text-[11px] text-ink-3'}>
            {hint}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}
